import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { X, Calendar, Clock, Star, Tv, ChevronRight, Loader2 } from 'lucide-react';
import { getMediaSeasons } from '../../services/mediaService';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 125;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
`;

const Dialog = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(980px, 100%);
  max-height: min(88vh, 920px);
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm || '12px'};
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
  overflow: hidden;
`;

const AccentBar = styled.div`
  height: 5px;
  background: linear-gradient(90deg, var(--primary, #003087) 0%, var(--crimson, #dc2626) 100%);
  flex-shrink: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.25rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.3;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm || '8px'};
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 0;
  flex: 1;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const SeasonNav = styled.nav`
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: rgba(0, 48, 135, 0.03);
  overflow-y: auto;
  padding: 0.75rem;

  @media (max-width: 720px) {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.65rem 0.75rem;
  }
`;

const SeasonBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  margin-bottom: 0.4rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  background: ${({ $active, theme }) => ($active ? 'rgba(0,48,135,0.08)' : theme.colors.card)};
  color: ${({ theme }) => theme.colors.foreground};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 720px) {
    width: auto;
    min-width: 140px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

const SeasonPoster = styled.div`
  width: 2.5rem;
  height: 3.5rem;
  flex-shrink: 0;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
`;

const SeasonBtnText = styled.div`
  min-width: 0;
`;

const SeasonBtnLabel = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.25;
`;

const SeasonBtnMeta = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const EpisodePanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const SeasonHeader = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

const SeasonTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
`;

const SeasonOverview = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EpisodeList = styled.div`
  overflow-y: auto;
  padding: 0.75rem 1rem 1rem;
  flex: 1;
`;

const EpisodeCard = styled.article`
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 0.85rem;
  padding: 0.75rem;
  margin-bottom: 0.65rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radiusSm || '10px'};
  background: ${({ theme }) => theme.colors.card};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: rgba(0, 48, 135, 0.35);
    box-shadow: ${({ theme }) => theme.shadowCard || '0 2px 8px rgba(0,0,0,0.06)'};
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const EpisodeStill = styled.div`
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.75rem;
  font-weight: 600;
`;

const EpisodeBody = styled.div`
  min-width: 0;
`;

const EpisodeTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
`;

const EpisodeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: rgba(0, 48, 135, 0.08);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  flex-shrink: 0;
`;

const EpisodeTitle = styled.h4`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.foreground};
`;

const EpisodeOverview = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const EpisodeMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const CenterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  text-align: center;
  min-height: 240px;
  grid-column: 1 / -1;
`;

const Spin = styled(Loader2)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function formatDate(value, locale) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  try {
    return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

function seasonLabel(season, t) {
  if (season.seasonNumber === 0) return t('media.detail.specials', 'Specials');
  if (season.name && !/^season\s+\d+$/i.test(season.name.trim())) {
    return season.name;
  }
  return `${t('media.detail.seasonLabel', 'Season')} ${season.seasonNumber}`;
}

function episodeBadge(episode, t) {
  if (episode.episodeNumber == null) return t('media.detail.episodeLabel', 'Episode');
  return `${t('media.detail.episodeLabel', 'Ep')} ${episode.episodeNumber}`;
}

export default function TvSeasonsModal({ open, onClose, category, mediaId, language, showTitle, t, locale }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeSeason, setActiveSeason] = useState(null);

  const loadSeasons = useCallback(async () => {
    if (!category || !mediaId) return;
    setLoading(true);
    setError('');
    try {
      const payload = await getMediaSeasons(category, mediaId, { language });
      setData(payload);
      const first = payload?.seasons?.[0];
      setActiveSeason(first?.seasonNumber ?? null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || t('media.detail.seasonsLoadFailed', 'Could not load seasons'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [category, mediaId, language, t]);

  useEffect(() => {
    if (!open) return undefined;
    loadSeasons();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, loadSeasons, onClose]);

  const seasons = data?.seasons || [];
  const selected = useMemo(
    () => seasons.find((s) => s.seasonNumber === activeSeason) || seasons[0] || null,
    [seasons, activeSeason],
  );

  const totalEpisodes = useMemo(
    () => seasons.reduce((sum, s) => sum + (s.episodes?.length || s.episodeCount || 0), 0),
    [seasons],
  );

  if (!open) return null;

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Dialog role="dialog" aria-modal="true" aria-label={t('media.detail.seasonsModalTitle', 'Seasons & episodes')} onClick={(e) => e.stopPropagation()}>
        <AccentBar />
        <Header>
          <HeaderText>
            <Title>{t('media.detail.seasonsModalTitle', 'Seasons & episodes')}</Title>
            <Subtitle>
              {showTitle || data?.name || ''}
              {seasons.length > 0
                ? ` · ${seasons.length} ${t('media.detail.seasons', 'Seasons').toLowerCase()} · ${totalEpisodes} ${t('media.detail.episodes', 'Episodes').toLowerCase()}`
                : ''}
            </Subtitle>
          </HeaderText>
          <CloseBtn type="button" onClick={onClose} aria-label={t('common.close', 'Close')}>
            <X size={18} />
          </CloseBtn>
        </Header>

        {loading ? (
          <CenterState>
            <Spin size={28} />
            <p>{t('media.detail.loadingSeasons', 'Loading seasons and episodes…')}</p>
          </CenterState>
        ) : error ? (
          <CenterState>
            <Tv size={28} style={{ opacity: 0.45 }} />
            <p>{error}</p>
          </CenterState>
        ) : !seasons.length ? (
          <CenterState>
            <Tv size={28} style={{ opacity: 0.45 }} />
            <p>{t('media.detail.noSeasons', 'No season data available.')}</p>
          </CenterState>
        ) : (
          <Body>
            <SeasonNav aria-label={t('media.detail.seasons', 'Seasons')}>
              {seasons.map((season) => {
                const active = selected?.seasonNumber === season.seasonNumber;
                return (
                  <SeasonBtn
                    key={season.seasonNumber}
                    type="button"
                    $active={active}
                    onClick={() => setActiveSeason(season.seasonNumber)}
                  >
                    <SeasonPoster $url={season.posterUrl} aria-hidden>
                      {!season.posterUrl ? <Tv size={16} style={{ margin: 'auto' }} /> : null}
                    </SeasonPoster>
                    <SeasonBtnText>
                      <SeasonBtnLabel>{seasonLabel(season, t)}</SeasonBtnLabel>
                      <SeasonBtnMeta>
                        {(season.episodes?.length || season.episodeCount || 0)} {t('media.detail.episodes', 'Episodes').toLowerCase()}
                      </SeasonBtnMeta>
                    </SeasonBtnText>
                    {active ? <ChevronRight size={14} style={{ marginLeft: 'auto', flexShrink: 0 }} /> : null}
                  </SeasonBtn>
                );
              })}
            </SeasonNav>

            <EpisodePanel>
              {selected ? (
                <>
                  <SeasonHeader>
                    <SeasonTitle>{seasonLabel(selected, t)}</SeasonTitle>
                    {selected.overview ? <SeasonOverview>{selected.overview}</SeasonOverview> : null}
                  </SeasonHeader>
                  <EpisodeList>
                    {(selected.episodes || []).length === 0 ? (
                      <CenterState style={{ minHeight: 180 }}>
                        <p>{t('media.detail.noEpisodes', 'No episode details for this season.')}</p>
                      </CenterState>
                    ) : (
                      selected.episodes.map((ep) => (
                        <EpisodeCard key={`${selected.seasonNumber}-${ep.episodeNumber}`}>
                          <EpisodeStill $url={ep.stillUrl} aria-hidden>
                            {!ep.stillUrl ? episodeBadge(ep, t) : null}
                          </EpisodeStill>
                          <EpisodeBody>
                            <EpisodeTop>
                              <EpisodeTitle>{ep.name || episodeBadge(ep, t)}</EpisodeTitle>
                              <EpisodeBadge>{episodeBadge(ep, t)}</EpisodeBadge>
                            </EpisodeTop>
                            {ep.overview ? <EpisodeOverview>{ep.overview}</EpisodeOverview> : null}
                            <EpisodeMeta>
                              {ep.airDate ? (
                                <MetaItem>
                                  <Calendar size={13} />
                                  {formatDate(ep.airDate, locale)}
                                </MetaItem>
                              ) : null}
                              {ep.runtime ? (
                                <MetaItem>
                                  <Clock size={13} />
                                  {ep.runtime} {t('media.detail.minutes', 'min')}
                                </MetaItem>
                              ) : null}
                              {ep.voteAverage != null && ep.voteAverage > 0 ? (
                                <MetaItem>
                                  <Star size={13} fill="currentColor" style={{ color: 'var(--gold)' }} />
                                  {Number(ep.voteAverage).toFixed(1)}/10
                                </MetaItem>
                              ) : null}
                            </EpisodeMeta>
                          </EpisodeBody>
                        </EpisodeCard>
                      ))
                    )}
                  </EpisodeList>
                </>
              ) : null}
            </EpisodePanel>
          </Body>
        )}
      </Dialog>
    </Overlay>
  );
}
